/**
 * Hook: useAppealUserReport
 *
 * Holds all state and executes the use case for the AppealUserReportSheet form.
 * Matches Flutter's UserReportAppealScreenState logic.
 */

import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { appealUserReportUseCase } from '@/domain/usecases/report/appealUserReportUseCase';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseAppealUserReportProps {
  onFinish: () => void;
}

export interface UseAppealUserReportResult {
  comment: string;
  isSubmitting: boolean;
  hasCommentError: boolean;
  setComment: (value: string) => void;
  handleSubmit: () => Promise<void>;
}

export function useAppealUserReport({ onFinish }: UseAppealUserReportProps): UseAppealUserReportResult {
  const { l10n } = useLocale();

  const [comment, setCommentState] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setComment = useCallback((value: string) => {
    setIsDirty(true);
    setCommentState(value);
  }, []);

  const hasCommentError = isDirty && comment.trim().length === 0;

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    if (comment.trim().length === 0) return;

    setIsSubmitting(true);
    try {
      const success = await appealUserReportUseCase.execute(comment);
      if (success) {
        toast.success(l10n.appealUserReport.submitSuccess);
        onFinish();
      } else {
        toast.error(l10n.appealUserReport.submitError);
      }
    } catch (err) {
      appLogger.error('Error submitting appeal', 'useAppealUserReport.handleSubmit', { error: err as Error });
      toast.error(l10n.appealUserReport.submitError);
    } finally {
      setIsSubmitting(false);
    }
  }, [comment, isSubmitting, onFinish, l10n]);

  return {
    comment,
    isSubmitting,
    hasCommentError,
    setComment,
    handleSubmit,
  };
}
